import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { InstallationFeaturesStore } from '@store';
import {
  ActiveModeCommands,
  Feature,
  FeatureActionFields,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  FrostProtectionCommands,
  FrostProtectionProperties,
  FrostProtectionType,
  IsActiveProperty,
  IsStringValueProperty,
  VentilationHeatExchangerFrostProtectionType,
} from '@vi-iot/client/index';
import { combineLatest, Observable, Subject } from 'rxjs';
import { GTMService } from '@services';
import { take, takeUntil } from 'rxjs/operators';
import { Option } from '../../../../../projects/ui/components/dropdown-autocomplete/dropdown-autocomplete.component';
import { ViConfirmDialogService } from '@vi-iot/ui/index';
import { Value2Pipe } from 'src/app/operational-data-common/feature-pipes/value-strategy-based/value2.pipe';
export interface OperationModeOption extends Option {
  tooltipContent: string;
}

@Component({
  selector: 'app-ventilation-frost-protection',
  templateUrl: './ventilation-frost-protection.component.html',
  styleUrls: ['./ventilation-frost-protection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VentilationFrostProtectionComponent implements OnInit, OnDestroy {
  @Input() data: Feature<FrostProtectionProperties, FrostProtectionCommands>;

  readonly ventilationHeatExchangerFrostProtectionType = VentilationHeatExchangerFrostProtectionType;
  readonly frostProtectionType = FrostProtectionType;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  ventilationHeatExchangerFrostProtection$: Observable<
    Feature<IsStringValueProperty, ActiveModeCommands>
  >;
  ventilationPreHeater$: Observable<Feature<IsActiveProperty, never>>;
  frostProtectionFilters: Option[] = [
    {
      value: FrostProtectionType.nonEnthalpy,
      viewValue: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.FROST_PROTECTION.TYPE.NONENTHALPY'
      ),
      isShow: true,
    },
    {
      value: FrostProtectionType.enthalpy,
      viewValue: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.FROST_PROTECTION.TYPE.ENTHALPY'
      ),
      isShow: true,
    },
  ];
  operatorModes: OperationModeOption[] = [
    {
      value: VentilationHeatExchangerFrostProtectionType.disbalance,
      viewValue: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.FROST_PROTECTION.OPERATION_MODES.DISBALANCE'
      ),
      isShow: true,
      isWrite: true,
      tooltipContent: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.FROST_PROTECTION.OPERATION_MODES.DISBALANCE_TOOLTIP'
      ),
    },
    {
      value: VentilationHeatExchangerFrostProtectionType.intermittent,
      viewValue: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.FROST_PROTECTION.OPERATION_MODES.INTERMITTENT'
      ),
      isShow: true,
      isWrite: true,
      tooltipContent: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.FROST_PROTECTION.OPERATION_MODES.INTERMITTENT_TOOLTIP'
      ),
    },
    {
      value: VentilationHeatExchangerFrostProtectionType.preheater,
      viewValue: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.FROST_PROTECTION.OPERATION_MODES.PREHEATER'
      ),
      isShow: true,
      isWrite: true,
      tooltipContent: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.FROST_PROTECTION.OPERATION_MODES.PREHEATER_TOOLTIP'
      ),
    },
    {
      value: VentilationHeatExchangerFrostProtectionType.preheaterEcoMode,
      viewValue: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.FROST_PROTECTION.OPERATION_MODES.PREHEATER_ECO'
      ),
      isShow: true,
      isWrite: true,
      tooltipContent: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.FROST_PROTECTION.OPERATION_MODES.PREHEATER_ECO_TOOLTIP'
      ),
    },
  ];
  selectedHeatExchangerType: string;
  previousHeatExchangerType: string;

  constructor(
    private installationFeaturesStore: InstallationFeaturesStore,
    private translateService: TranslateService,
    private gtmService: GTMService,
    private viConfirmModalService: ViConfirmDialogService,
    private value2Pipe: Value2Pipe,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.ventilationHeatExchangerFrostProtection$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationHeatExchangerFrostprotectionOperatingModesActive
    );

    this.ventilationPreHeater$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationPreHeater
    );

    if (this.data) {
      this.selectedHeatExchangerType = this.value2Pipe.transform(
        this.data,
        FeaturePropertyKey.type
      );
      this.previousHeatExchangerType = this.value2Pipe.transform(
        this.data,
        FeaturePropertyKey.type
      );
      this.frostProtectionFilters = this.frostProtectionFilters.map((item) => {
        if (!this.data.commands.setType.params.type.constraints.enum.includes(item.value)) {
          return {
            ...item,
            isShow: false,
          };
        }

        return item;
      });
    }

    combineLatest([this.ventilationHeatExchangerFrostProtection$, this.ventilationPreHeater$])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([ventilationHeatExchangerFrostprotection, ventilationPreHeater]) => {
        const listOperationModeValues =
          ventilationHeatExchangerFrostprotection.commands.setMode.params.mode.constraints.enum;
        this.operatorModes = this.operatorModes.map((item) => {
          if (!listOperationModeValues.includes(item.value)) {
            if (!ventilationPreHeater.properties.active.value) {
              return {
                ...item,
                isWrite: false,
                isShow: true,
              };
            } else {
              return {
                ...item,
                isWrite: false,
                isShow: false,
              };
            }
          }

          return item;
        });
      });
  }

  onSelectFrostProtectionType(option: { value: FrostProtectionType }): void {
    this.viConfirmModalService
      .open({
        title: this.translateService.instant(
          this.translateService.instant(
            '+VENTILATION.REMOTE_CONTROLLER.HEAT_EXCHANGER_TYPE_CONFIRMATION.HEADER'
          )
        ),
        content: this.translateService.instant(
          this.translateService.instant(
            '+VENTILATION.REMOTE_CONTROLLER.HEAT_EXCHANGER_TYPE_CONFIRMATION.CONTENT'
          )
        ),
        confirmBtn: this.translateService.instant('GENERAL.BUTTON.CONFIRM'),
        cancelBtn: this.translateService.instant('GENERAL.BUTTON.CANCEL'),
      })
      .pipe(take(1))
      .subscribe((conditionAccepted) => {
        if (conditionAccepted) {
          this.installationFeaturesStore.updateFeature(
            FeatureNames.ventilationHeatExchangerType,
            option.value,
            FeaturePropertyKey.type,
            FeatureActionFields.type,
            FeatureCommands.setType
          );
          this.gtmService.track('onSelect', 'frost protection type', 'frostProtection');
          this.previousHeatExchangerType = option.value;
        } else {
          this.selectedHeatExchangerType = this.previousHeatExchangerType;
          this.cdr.detectChanges();
        }
      });
  }

  onOperationModeChange(value: VentilationHeatExchangerFrostProtectionType): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.ventilationHeatExchangerFrostprotectionOperatingModesActive,
      value,
      FeaturePropertyKey.value,
      FeatureActionFields.mode,
      FeatureCommands.setMode
    );
    this.gtmService.track('onSelect', 'frost protection operation`s mode', 'frostProtection');
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
